import { useEffect } from 'react';

/**
 * SEO Component - Updates document title and meta tags
 * Usage: <SEO title="Page Title" description="Page description" />
 */
export default function SEO({ title, description, keywords }) {
    useEffect(() => {
        // Update document title
        document.title = title;
        
        // Update meta description
        let metaDescription = document.querySelector('meta[name="description"]');
        if (!metaDescription) {
            metaDescription = document.createElement('meta');
            metaDescription.name = 'description';
            document.head.appendChild(metaDescription);
        }
        metaDescription.content = description;
        
        // Update meta keywords if provided
        if (keywords) {
            let metaKeywords = document.querySelector('meta[name="keywords"]');
            if (!metaKeywords) {
                metaKeywords = document.createElement('meta');
                metaKeywords.name = 'keywords';
                document.head.appendChild(metaKeywords);
            }
            metaKeywords.content = keywords;
        }
        
        // Update Open Graph tags
        let ogTitle = document.querySelector('meta[property="og:title"]');
        if (!ogTitle) {
            ogTitle = document.createElement('meta');
            ogTitle.setAttribute('property', 'og:title');
            document.head.appendChild(ogTitle);
        }
        ogTitle.content = title;
        
        let ogDescription = document.querySelector('meta[property="og:description"]');
        if (!ogDescription) {
            ogDescription = document.createElement('meta');
            ogDescription.setAttribute('property', 'og:description');
            document.head.appendChild(ogDescription);
        }
        ogDescription.content = description;
        
        // Update Twitter Card tags
        let twitterTitle = document.querySelector('meta[name="twitter:title"]');
        if (!twitterTitle) {
            twitterTitle = document.createElement('meta');
            twitterTitle.name = 'twitter:title';
            document.head.appendChild(twitterTitle);
        }
        twitterTitle.content = title;
        
        let twitterDescription = document.querySelector('meta[name="twitter:description"]');
        if (!twitterDescription) {
            twitterDescription = document.createElement('meta');
            twitterDescription.name = 'twitter:description';
            document.head.appendChild(twitterDescription);
        }
        twitterDescription.content = description;
        
    }, [title, description, keywords]);
    
    return null; // This component doesn't render anything
}

// SEO configurations for all pages
export const SEO_CONFIG = {
    home: {
        title: "AI Website Audit Tool | Free Website Grader | SiteGradePro",
        description: "Get a free AI-powered website audit in seconds. SiteGradePro analyzes SEO, performance, mobile optimization, and conversions — and gives you a clear letter grade with actionable fixes.",
        keywords: "AI website audit tool, free website grader, SEO analysis, website score, site audit"
    },
    features: {
        title: "Website Audit Features | SEO, Speed & UX Analysis | SiteGradePro",
        description: "Explore SiteGradePro's powerful website audit features including SEO analysis, speed insights, mobile optimization checks, and conversion improvement recommendations.",
        keywords: "website audit features, SEO analysis, speed optimization, mobile optimization, UX analysis"
    },
    howItWorks: {
        title: "How SiteGradePro Works | Instant Website Score in 3 Steps",
        description: "Learn how SiteGradePro analyzes your website in seconds and provides a clear grade with prioritized recommendations to improve SEO, speed, and conversions.",
        keywords: "how website audit works, website analysis process, instant website score"
    },
    pricing: {
        title: "Website Audit Pricing | Free & Pro Plans | SiteGradePro",
        description: "Choose the right website audit plan for your business. Start free or upgrade to Pro for unlimited audits, full SEO breakdowns, and downloadable reports.",
        keywords: "website audit pricing, free website audit, pro website audit, audit plans"
    },
    about: {
        title: "About SiteGradePro | AI Website Optimization Tool",
        description: "Discover the mission behind SiteGradePro — helping small businesses improve their websites with affordable, AI-powered audits and clear action steps.",
        keywords: "about SiteGradePro, website optimization tool, small business website audit"
    },
    contact: {
        title: "Contact SiteGradePro | Support & Feature Requests",
        description: "Need help with your website audit? Contact SiteGradePro for support, feature suggestions, or business inquiries.",
        keywords: "contact SiteGradePro, website audit support, feature requests"
    },
    blog: {
        title: "Website Optimization Blog | Tips & Guides | SiteGradePro",
        description: "Expert tips, guides, and insights to help you improve your website's performance, SEO, and conversions.",
        keywords: "website optimization blog, SEO tips, website improvement guides"
    }
};
